import { Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { SelectItem, SelectItemGroup, ConfirmationService } from 'primeng/api';
import { AcModel } from 'projects/core/src/lib/models/ac.model';
import { BusinessunitModel } from 'projects/core/src/lib/models/businessunit.model';
import { LocationModel } from 'projects/core/src/lib/models/location.model';
import { RoomModel } from 'projects/core/src/lib/models/room.model';
import { TemplateModel } from 'projects/core/src/lib/models/template.model';
import { UserModel } from 'projects/core/src/lib/models/user.model';
import { HelperService } from './helper.service';
import { AnonymizedCandidatePipe } from 'projects/core/src/lib/pipes/anonymized-candidate';
import { AuthStateService } from './auth-state.service';

@Injectable({
  providedIn: 'root'
})
export class OrderHelperService {

  constructor(
    private transloco: TranslocoService,
    private confirmation: ConfirmationService,
    private helper: HelperService,
    private auth: AuthStateService
  ) { }

  public getTemplates(templates: Array<any>): Array<TemplateModel> {
    return templates.map((template: any) => {
      return new TemplateModel().deserialize(template);
    }).sort((a: TemplateModel, b: TemplateModel) => {
      return (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0);
    });
  }

  public getMmappedTexts(texts: any): any {
    const result: any  = {};
    Object.keys(texts).forEach((key: string) => {
      result[key] = '';
      const lang = this.transloco.getActiveLang().toUpperCase();
      if (texts[key] && texts[key][lang]) {
        result[key] = texts[key][lang];
      }
    });
    return result;
  }

  public getMappedOptions(options: any): any {
    const result: any = {};
    Object.keys(options).forEach((key: string) => {
      if (Array.isArray(options[key])) {
        if (key === 'businessunits') {
          result[key] = this.mapBusinessUnits(options[key]);
        } else {
          let orderCol = 'value';
          result[key] = options[key].map((option: any) => {
            switch (key) {
              case 'candidatetype': {
                return {
                  value: option,
                  label: this.transloco.translate('common.options.' + key + '.' + option)
                };
              }
              case 'locations': {
                orderCol = 'name';
                return new LocationModel().deserialize(option);
              }
              case 'rooms': {
                orderCol = 'name';
                return new RoomModel().deserialize(option);
              }
              case 'dispatchers': {
                orderCol = 'fullname_reverse';
                return new UserModel().deserialize(option);
              }
              default: {
                return option;
              }
            }
          }).sort((a: any, b: any) => {
            return this.helper.getSortValue(a[orderCol], b[orderCol]);
          });
        }
      } else {
        result[key] = [];
      }
    });
    return result;
  }

  public mapBusinessUnits(businessunits: Array<any>): Array<SelectItemGroup> {
    const options: Array<SelectItemGroup> = [];
    businessunits = businessunits.map((businessunit: any) => {
      return new BusinessunitModel().deserialize(businessunit);
    }).sort((a: BusinessunitModel, b: BusinessunitModel) => {
      return (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0);
    });
    businessunits.forEach((businessunit: BusinessunitModel) => {
      const children: SelectItem[] = [];
      if (businessunit.businessunits?.length > 0) {
        businessunit.businessunits.map((child: any) => {
          return new BusinessunitModel().deserialize(child);
        }).sort((a: BusinessunitModel, b: BusinessunitModel) => {
          return (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0);
        }).forEach((childunit: BusinessunitModel) => {
          children.push({
            value: childunit,
            label: childunit.name
          });
        });
      }
      if (children.length > 0) {
        options.push({
          value: businessunit,
          label: businessunit.name,
          items: children
        });
      }
    });
    return options;
  }

  // special functions

  public showNodatesInfo(order: AcModel | any, atOrder = false): Promise<void> {
    return new Promise((resolve) => {
      this.confirmation.confirm({
          header: (atOrder === true) ? this.transloco.translate('common.error') : this.transloco.translate('common.info'),
          message: this.getNodatesContent(order, atOrder, true),
          rejectLabel: (atOrder === true) ? this.transloco.translate('common.close') : '',
          rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
          rejectVisible: !!atOrder,
          acceptLabel: (atOrder === true) ? this.transloco.translate('common.order_anyway') : this.transloco.translate('common.ok'),
          icon: (atOrder === true) ? 'pi pi-times-circle' : 'pi pi-exclamation-circle',
          accept: () => {
            resolve();
          },
          reject: () => {}
      });
    });
  }

  public getNodatesContent(order: AcModel | any, atOrder = false, html = true): string {
    const infos: Array<string> = [];
    if (atOrder) {
      infos.push('<p style="color: red;"><strong>' + this.transloco.translate('helpers.order_not_possible') + '</strong></p>');
    }
    // missing
    if (Array.isArray(order.clearing_info?.missing) && order.clearing_info?.missing.length > 0) {
      infos.push('<p><strong>' + this.transloco.translate('helpers.order_clearing') + '</strong></p>');
      const resources: Array<string> = [];
      order.clearing_info?.missing.forEach((entry: any) => {
        resources.push('<li>' + this.transloco.translate('common.labels.' + entry) + '</li>');
      });
      if (resources.length > 0) {
        infos.push('<ul>' + resources.join('') + '</ul>');
      }
    }
    // no dates
    if (Array.isArray(order.clearing_info?.nodates) && order.clearing_info?.nodates.length > 0) {
      if (order.start_time) {
        infos.push('<p><strong>' + this.transloco.translate('helpers.order_nodates', { date: moment(order.start_time).format('DD.MM.YYYY') }) + '</strong></p>');
        const resources: Array<string> = [];
        order.clearing_info?.nodates.forEach((entry: any) => {
          const timeList = [];
          let nodateContent = '<li>' + this.transloco.translate('common.labels.' + entry.type) + ': ' + entry.name;
          if (Array.isArray(entry.times) && entry.times.length > 0) {
            entry.times.forEach((time: any) => { // @AP-411 optimize?
              const start = moment.utc(new Date().toISOString().split('T')[0] + ' ' + time.start);
              const end = moment.utc(new Date().toISOString().split('T')[0] + ' ' + time.end);
              timeList.push(start.tz('Europe/Berlin').format('HH:mm') + ' - ' + end.tz('Europe/Berlin').format('HH:mm') + ' MESZ/MEZ'); // @AP-411 übersetzen
            });
          }
          if (timeList.length > 0) {
            nodateContent += ' (' + this.transloco.translate('common.labels.available') + ': ';
            nodateContent += timeList.join(', ');
            nodateContent += ')';
          }
          nodateContent += '</li>';
          resources.push(nodateContent);
        });
        if (resources.length > 0) {
          infos.push('<ul>' + resources.join('') + '</ul>');
        }
      }
    }
    // no locations
    if (Array.isArray(order.clearing_info?.nolocations) && order.clearing_info?.nolocations.length > 0) {
        infos.push('<p><strong>' + this.transloco.translate('helpers.order_nolocations') + '</strong></p>');
        const resources: Array<string> = [];
        order.clearing_info?.nolocations.forEach((entry: any) => {
          resources.push('<li>' + this.transloco.translate('common.labels.' + entry.type) + ': ' + entry.name + '</li>');
        });
        if (resources.length > 0) {
          infos.push('<ul>' + resources.join('') + '</ul>');
        }
    }
    // double bookings
    if (Array.isArray(order.clearing_info?.doublebookings) && order.clearing_info?.doublebookings.length > 0) {
      infos.push('<p><strong>' + this.transloco.translate('helpers.order_doublebookings') + '</strong></p>');
      const resources: Array<string> = [];
      const url = window.location.origin + '/planning/orders/';
      order.clearing_info?.doublebookings.forEach((entry: any) => {
        const candidateName = this.auth.getAnonymizedValue(entry.candidate_name);
        resources.push('<li>' + candidateName + ': <strong><a href="' + url + entry.ac_id + '" target="_blank">' + entry.ac_ordernumber + '</a></strong> - ' + moment(entry.ac_start).format('D.M.Y') + ' - ' + entry.ac_name + '</li>');
      });
      if (resources.length > 0) {
        infos.push('<ul>' + resources.join('') + '</ul>');
      }
    }
    // incomplete todos
    if (order.clearing_info?.todos) {
      infos.push('<p><strong>' + this.transloco.translate('helpers.order_incomplete_todos', { amount: order.clearing_info.todos}) + '</strong></p>');
    }
    if (html === false) {
      return infos.join('').replace(/(<([^>]+)>)/gi, ' ');
    }
    return infos.join('');
  }

}
